import { useEffect, useRef, useState } from 'react';
import { sendPtzCommand } from '../../services/cameraService';

/**
 * Streams JPEG frames from the backend WebSocket for a given cameraId.
 * Renders each frame on a <canvas> element.
 * Optionally shows PTZ controls (if the camera supports ONVIF PTZ).
 * Optionally streams audio via a second WebSocket + MediaSource API.
 *
 * Props:
 *   cameraId  - number
 *   name      - string (display name overlay)
 *   camera    - full camera object (optional; needed for PTZ detection)
 */
function CameraViewer({ cameraId, name, camera }) {
    const canvasRef = useRef(null);
    const wsRef = useRef(null);
    const mountedRef = useRef(true);
    const [status, setStatus] = useState('connecting'); // connecting | connected | error | disconnected
    const [errorMsg, setErrorMsg] = useState('');

    // PTZ state
    const [ptzSupported, setPtzSupported] = useState(null); // null=unknown, true, false

    // Audio state
    const [audioEnabled, setAudioEnabled] = useState(false);
    const [audioStatus, setAudioStatus] = useState('idle'); // idle | connecting | active | error
    const [audioError, setAudioError] = useState('');
    const audioWsRef = useRef(null);
    const mediaSourceRef = useRef(null);
    const sourceBufferRef = useRef(null);
    const audioQueueRef = useRef([]);
    const audioElementRef = useRef(null);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // Video WebSocket
    useEffect(() => {
        if (!cameraId) return;

        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://myindustrialdata-backend.pritech.com.pe';
        const wsProto = backendUrl.startsWith('https') ? 'wss' : 'ws';
        const wsUrl = wsProto + backendUrl.slice(backendUrl.indexOf('://')) + `/ws/camera/${cameraId}/`;

        const ws = new WebSocket(wsUrl);
        ws.binaryType = 'arraybuffer';
        wsRef.current = ws;

        const safeSetStatus = (s) => { if (mountedRef.current) setStatus(s); };

        ws.onopen = () => {
            safeSetStatus('connecting');
        };

        ws.onmessage = (event) => {
            if (typeof event.data === 'string') {
                try {
                    const msg = JSON.parse(event.data);
                    if (msg.type === 'status' && msg.message === 'connected') {
                        safeSetStatus('connected');
                    } else if (msg.type === 'error') {
                        setErrorMsg(msg.message || '');
                        safeSetStatus('error');
                    }
                } catch (_) {}
                return;
            }

            // Binary frame
            const blob = new Blob([event.data], { type: 'image/jpeg' });
            const url = URL.createObjectURL(blob);
            const img = new Image();
            img.onload = () => {
                const canvas = canvasRef.current;
                if (canvas) {
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                }
                URL.revokeObjectURL(url);
                safeSetStatus('connected');
            };
            img.src = url;
        };

        ws.onerror = () => {
            safeSetStatus('error');
        };

        ws.onclose = () => {
            safeSetStatus('disconnected');
        };

        return () => {
            ws.onopen = null;
            ws.onmessage = null;
            ws.onerror = null;
            ws.onclose = null;
            ws.close();
        };
    }, [cameraId]);

    // PTZ support: show controls whenever the camera has a ptz_profile_token configured
    useEffect(() => {
        setPtzSupported(!!camera?.ptz_profile_token);
    }, [cameraId, camera]);

    // PTZ handlers
    const SPEED = 1.0;
    const startMove = (pan, tilt, zoom) => {
        if (status !== 'connected') return;
        sendPtzCommand(cameraId, 'move', pan * SPEED, tilt * SPEED, zoom * SPEED).catch(() => {});
    };
    const stopMove = () => {
        sendPtzCommand(cameraId, 'stop').catch(() => {});
    };

    // Audio WebSocket + MediaSource
    useEffect(() => {
        if (!audioEnabled || !cameraId) {
            if (audioWsRef.current) {
                audioWsRef.current.close();
                audioWsRef.current = null;
            }
            if (mountedRef.current) setAudioStatus('idle');
            return;
        }
        setAudioError('');

        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://myindustrialdata-backend.pritech.com.pe';
        const wsProto = backendUrl.startsWith('https') ? 'wss' : 'ws';
        const wsUrl = wsProto + backendUrl.slice(backendUrl.indexOf('://')) + `/ws/camera/${cameraId}/audio/`;

        const ms = new MediaSource();
        mediaSourceRef.current = ms;
        const audioEl = audioElementRef.current;
        const blobUrl = URL.createObjectURL(ms);
        if (audioEl) audioEl.src = blobUrl;

        let isActive = true;
        let sb = null;

        // Jump to the latest buffered range — called only when audio actually stalls
        const jumpToBuffer = () => {
            const el = audioElementRef.current;
            if (!el || el.buffered.length === 0) return;
            const lastIdx = el.buffered.length - 1;
            const latestStart = el.buffered.start(lastIdx);
            if (el.currentTime < latestStart) {
                el.currentTime = latestStart;
            }
        };

        // Central drain function — only appends when SourceBuffer is truly ready
        const drainQueue = () => {
            if (!sb || sb.updating || ms.readyState !== 'open') return;
            if (audioQueueRef.current.length === 0) return;
            try {
                sb.appendBuffer(audioQueueRef.current.shift());
            } catch (_) {
                audioQueueRef.current = []; // clear on error to avoid cascading failures
            }
        };

        ms.addEventListener('sourceopen', () => {
            if (!isActive) return;
            try {
                sb = ms.addSourceBuffer('audio/webm; codecs="opus"');
                sourceBufferRef.current = sb;
                sb.addEventListener('updateend', () => { jumpToBuffer(); drainQueue(); });
                drainQueue(); // flush any chunks that arrived before sourceopen
                if (audioEl) {
                    audioEl.onwaiting = () => jumpToBuffer();
                    audioEl.onstalled = () => jumpToBuffer();
                    audioEl.onplaying = () => { audioEl.muted = false; };
                    // Fallback: force unmute after 2s in case 'playing' event never fires
                    const unmuteTimer = setTimeout(() => {
                        if (audioEl.muted) audioEl.muted = false;
                    }, 2000);
                    audioEl.muted = true;
                    audioEl.play()
                        .catch(() => { clearTimeout(unmuteTimer); });
                }
            } catch (e) {
                if (mountedRef.current) {
                    setAudioStatus('idle');
                    setAudioEnabled(false);
                    setAudioError('Browser does not support WebM/Opus audio');
                }
            }
        });

        const ws = new WebSocket(wsUrl);
        ws.binaryType = 'arraybuffer';
        audioWsRef.current = ws;

        ws.onmessage = (event) => {
            if (typeof event.data === 'string') {
                try {
                    const msg = JSON.parse(event.data);
                    if (msg.type === 'status') {
                        if (mountedRef.current) setAudioStatus('active');
                    }
                    if (msg.type === 'error') {
                        if (mountedRef.current) {
                            setAudioStatus('idle');
                            setAudioEnabled(false);
                            setAudioError(msg.message || 'Audio stream failed');
                        }
                    }
                } catch (_) {}
                return;
            }
            // Binary chunk: queue it then try to drain
            if (audioQueueRef.current.length > 20) audioQueueRef.current.shift();
            audioQueueRef.current.push(event.data);
            drainQueue();
        };

        ws.onopen = () => { if (mountedRef.current) setAudioStatus('connecting'); };
        ws.onclose = () => {
            if (mountedRef.current && isActive) {
                setAudioStatus('idle');
                setAudioEnabled(false);
            }
        };
        ws.onerror = () => {
            if (mountedRef.current) {
                setAudioStatus('idle');
                setAudioEnabled(false);
                setAudioError('Could not connect to audio stream');
            }
        };

        return () => {
            isActive = false;
            ws.onopen = null;
            ws.onmessage = null;
            ws.onerror = null;
            ws.onclose = null;
            ws.close();
            audioWsRef.current = null;
            URL.revokeObjectURL(blobUrl);
            audioQueueRef.current = [];
            sourceBufferRef.current = null;
        };
    }, [audioEnabled, cameraId]);

    return (
        <div style={{ position: 'relative', background: '#000', borderRadius: 4, overflow: 'hidden' }}>
            {name && (
                <div style={{
                    position: 'absolute', top: 6, left: 8, zIndex: 2,
                    background: 'rgba(0,0,0,0.55)', color: '#fff',
                    fontSize: 12, padding: '2px 6px', borderRadius: 3
                }}>
                    {name}
                </div>
            )}

            {/* Audio toggle button */}
            <button
                onClick={() => {
                    setAudioError('');
                    setAudioEnabled(e => !e);
                }}
                style={{
                    position: 'absolute', top: 6, right: 8, zIndex: 3,
                    background: 'rgba(0,0,0,0.55)', border: 'none',
                    color: audioError ? '#ffc107' : '#fff',
                    borderRadius: 3, padding: '2px 7px',
                    fontSize: 12, cursor: 'pointer'
                }}
                title={audioError || (audioEnabled ? 'Mute audio' : 'Enable audio')}
            >
                <i className={`fas fa-${audioEnabled ? 'volume-up' : 'volume-mute'} me-1`} />
                {audioStatus === 'connecting' ? 'Connecting...' : audioEnabled ? 'Audio On' : 'Audio Off'}
            </button>

            {status === 'connecting' && (
                <div style={overlayStyle}>
                    <span className="spinner-border spinner-border-sm text-light me-2" />
                    Connecting...
                </div>
            )}
            {status === 'error' && (
                <div style={{ ...overlayStyle, flexDirection: 'column', gap: 6, padding: '8px 12px', textAlign: 'center' }}>
                    <div><i className="fas fa-exclamation-triangle text-warning me-2" />Connection error</div>
                    {errorMsg && <div style={{ fontSize: 11, color: '#ccc', wordBreak: 'break-word' }}>{errorMsg}</div>}
                </div>
            )}
            {status === 'disconnected' && (
                <div style={overlayStyle}>
                    <i className="fas fa-video-slash text-secondary me-2" />
                    Disconnected
                </div>
            )}

            <canvas
                ref={canvasRef}
                style={{
                    display: 'block',
                    width: '100%',
                    minHeight: 180,
                    background: '#111'
                }}
            />

            {/* PTZ D-pad overlay */}
            {ptzSupported === true && (
                <div style={{
                    position: 'absolute', bottom: 8, right: 8, zIndex: 3,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 36px)',
                    gridTemplateRows: 'repeat(3, 36px)',
                    gap: 2,
                    opacity: status === 'connected' ? 1 : 0.4,
                }}>
                    {/* Row 1 */}
                    <div style={{ gridColumn: 1 }} />
                    <PtzBtn icon="fa-arrow-up"    onStart={() => startMove(0, 1, 0)}  onStop={stopMove} style={{ gridColumn: 2 }} title="Tilt Up" />
                    <div style={{ gridColumn: 3 }} />
                    {/* Row 2 */}
                    <PtzBtn icon="fa-arrow-left"  onStart={() => startMove(-1, 0, 0)} onStop={stopMove} style={{ gridColumn: 1 }} title="Pan Left" />
                    <div style={{ gridColumn: 2, background: 'rgba(0,0,0,0.5)', borderRadius: 3 }} />
                    <PtzBtn icon="fa-arrow-right" onStart={() => startMove(1, 0, 0)}  onStop={stopMove} style={{ gridColumn: 3 }} title="Pan Right" />
                    {/* Row 3 */}
                    <div style={{ gridColumn: 1 }} />
                    <PtzBtn icon="fa-arrow-down"  onStart={() => startMove(0, -1, 0)} onStop={stopMove} style={{ gridColumn: 2 }} title="Tilt Down" />
                    <div style={{ gridColumn: 3 }} />
                </div>
            )}

            {/* Hidden audio element */}
            <audio ref={audioElementRef} autoPlay playsInline style={{ display: 'none' }} />
        </div>
    );
}

function PtzBtn({ icon, onStart, onStop, style, title }) {
    return (
        <button
            title={title}
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onStart(); }}
            onMouseUp={onStop}
            onMouseLeave={onStop}
            onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); onStart(); }}
            onTouchEnd={onStop}
            style={{
                background: 'rgba(0,0,0,0.6)',
                border: '1px solid rgba(255,255,255,0.25)',
                borderRadius: 3,
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                userSelect: 'none',
                ...style,
            }}
        >
            <i className={`fas ${icon}`} />
        </button>
    );
}

const overlayStyle = {
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 14,
    background: 'rgba(0,0,0,0.7)'
};

export default CameraViewer;
